// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import CustomProductAddToCartButton from "@tps/App/Components/Product/CustomProductAddToCartButton";
import CustomProductQuantityOrdered from "@tps/App/Components/Product/CustomProductQuantityOrdered";
import { CustomHasProductContext, withProductContext } from "@tps/App/Store/Contexts/CustomProductContext";
import { xScreen } from "@tps/XDL/Utilities";
import { mergeWithCss } from "@tps/XDL/Utilities/mergeWithCss";
import * as React from "react";
import { FC, useState } from "react";
import { css } from "styled-components";

export interface ProductActionsProps {
    showActions?: boolean;
    showNumberInCart?: boolean;
    extendedStyles?: ProductActionsStyle;
    onAddToCartClick?: () => void;
}

type Props = ProductActionsProps & CustomHasProductContext;

export interface ProductActionsStyle {
    addToCartContainer?: GridContainerProps;
    qtyWrapper?: GridItemProps;
    addToCartButtonWrapper?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
}

const productActionsStyle: ProductActionsStyle = {
    addToCartContainer: {
        css: css`
            display: flex;
            align-items: center;
            gap: 12px;
            flex-wrap: nowrap;
        `,
    },
    qtyWrapper: {
        css: css`
            max-width: 80px;
        `,
    },
    addToCartButtonWrapper: {
        css: css`
            width: 137px;
            ${xScreen("lte", "md")} {
                flex-grow: 1;
            }
        `,
    },
    productAddToCartButtonStyles: {
        mergeCss: true,
        sizeVariant: "small",
        css: css`
            width: 100%;
        `,
    },
};

const ProductActions: FC<Props> = ({ extendedStyles, showActions = true, onAddToCartClick, productContext, showNumberInCart=false }) => {
    const [styles] = useState(() => mergeWithCss(productActionsStyle, extendedStyles));
    return (
        <StyledWrapper {...styles.addToCartContainer} data-test-selector="product_actions">
            <StyledWrapper {...styles.qtyWrapper}>
                <CustomProductQuantityOrdered id={productContext.product.id} />
            </StyledWrapper>
            <StyledWrapper {...styles.addToCartButtonWrapper}>
                <CustomProductAddToCartButton
                    onAddToCartClick={onAddToCartClick}
                    extendedStyles={styles.productAddToCartButtonStyles}
                    data-test-selector="addToCartBtn"
                    showNumberInCart={showNumberInCart}
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProductContext(ProductActions);
